class FilterDrawer extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-filter-form]');
    this.overlay = this.querySelector('.filter-drawer__overlay');
    this.panel = this.querySelector('.filter-drawer__panel');
    this.applyBtn = this.querySelector('.filter-drawer__apply');
    this.applyLabel = this.querySelector('[data-filter-apply-label]');
    this.spinner = this.querySelector('.filter-drawer__spinner');
    this.clearBtn = this.querySelector('[data-filter-clear]');

    this.sectionEl = this.closest('[data-filter-section]');
    this.sectionId = this.sectionEl ? this.sectionEl.getAttribute('data-filter-section') : null;

    this.bindOpenTriggers();
    this.bindCloseTriggers();
    this.bindForm();
    this.bindPills();
    this.bindClear();
    this.bindPriceSlider();
  }

  bindOpenTriggers() {
    document.querySelectorAll('[data-filter-drawer-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(btn);
      });
    });
  }

  bindCloseTriggers() {
    this.querySelectorAll('[data-filter-drawer-close]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.getAttribute('data-drawer-state') === 'open') this.close();
    });
  }

  bindForm() {
    if (!this.form) return;
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submit();
    });
  }

  bindPills() {
    // Toggle .is-active on pills/swatches/sort rows for immediate visual feedback
    this.querySelectorAll('.filter-drawer__pill input, .filter-drawer__swatch input').forEach((input) => {
      input.addEventListener('change', () => {
        const parent = input.closest('.filter-drawer__pill, .filter-drawer__swatch');
        if (!parent) return;
        parent.classList.toggle('is-active', input.checked);
      });
    });
    this.querySelectorAll('.filter-drawer__sort-item input').forEach((input) => {
      input.addEventListener('change', () => {
        this.querySelectorAll('.filter-drawer__sort-item').forEach((el) => el.classList.remove('is-selected'));
        const parent = input.closest('.filter-drawer__sort-item');
        if (parent && input.checked) {
          parent.classList.add('is-selected');
          const label = parent.getAttribute('data-sort-label') || parent.textContent.trim();
          const current = this.querySelector('[data-sort-current]');
          if (current) current.textContent = label;
          const dropdown = parent.closest('.filter-drawer__sort-dropdown');
          if (dropdown) dropdown.removeAttribute('open');
        }
      });
    });
  }

  bindClear() {
    if (!this.clearBtn) return;
    this.clearBtn.addEventListener('click', () => {
      this.form.querySelectorAll('input[type="checkbox"]').forEach((i) => {
        i.checked = false;
        const parent = i.closest('.filter-drawer__pill, .filter-drawer__swatch');
        if (parent) parent.classList.remove('is-active');
      });
      const priceMin = this.form.querySelector('[data-price-min]');
      const priceMax = this.form.querySelector('[data-price-max]');
      if (priceMin) priceMin.value = priceMin.min || '';
      if (priceMax) priceMax.value = priceMax.max || '';
      this.updatePriceSlider();
      this.submit();
    });
  }

  bindPriceSlider() {
    const slider = this.querySelector('[data-price-slider]');
    if (!slider) return;
    this.priceSlider = slider;
    this.priceRangeEl = slider.querySelector('[data-price-range]');
    this.priceThumbs = {
      min: slider.querySelector('[data-price-thumb="min"]'),
      max: slider.querySelector('[data-price-thumb="max"]'),
    };
    const wrap = slider.closest('[data-filter-price]');
    this.priceMinAbs = parseFloat(wrap.dataset.min) || 0;
    this.priceMaxAbs = parseFloat(wrap.dataset.max) || 0;
    this.priceMinInput = this.form.querySelector('[data-price-min]');
    this.priceMaxInput = this.form.querySelector('[data-price-max]');

    this.updatePriceSlider();
    [this.priceMinInput, this.priceMaxInput].forEach((inp) => {
      if (!inp) return;
      inp.addEventListener('change', () => this.updatePriceSlider());
      inp.addEventListener('input', () => this.updatePriceSlider());
    });

    Object.keys(this.priceThumbs).forEach((which) => {
      const thumb = this.priceThumbs[which];
      if (!thumb) return;
      const onDown = (e) => {
        e.preventDefault();
        const move = (ev) => this.onThumbDrag(ev, which);
        const up = () => {
          document.removeEventListener('mousemove', move);
          document.removeEventListener('touchmove', move);
          document.removeEventListener('mouseup', up);
          document.removeEventListener('touchend', up);
        };
        document.addEventListener('mousemove', move);
        document.addEventListener('touchmove', move, { passive: false });
        document.addEventListener('mouseup', up);
        document.addEventListener('touchend', up);
      };
      thumb.addEventListener('mousedown', onDown);
      thumb.addEventListener('touchstart', onDown, { passive: false });
    });
  }

  onThumbDrag(ev, which) {
    if (ev.cancelable) ev.preventDefault();
    const rect = this.priceSlider.getBoundingClientRect();
    const x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const value = Math.round(this.priceMinAbs + ratio * (this.priceMaxAbs - this.priceMinAbs));
    if (which === 'min') {
      const maxVal = parseFloat(this.priceMaxInput.value) || this.priceMaxAbs;
      this.priceMinInput.value = Math.min(value, maxVal);
    } else {
      const minVal = parseFloat(this.priceMinInput.value) || this.priceMinAbs;
      this.priceMaxInput.value = Math.max(value, minVal);
    }
    this.updatePriceSlider();
  }

  updatePriceSlider() {
    if (!this.priceSlider) return;
    const range = this.priceMaxAbs - this.priceMinAbs || 1;
    const minVal = parseFloat(this.priceMinInput.value);
    const maxVal = parseFloat(this.priceMaxInput.value);
    const safeMin = isNaN(minVal) ? this.priceMinAbs : minVal;
    const safeMax = isNaN(maxVal) ? this.priceMaxAbs : maxVal;
    const minPct = ((safeMin - this.priceMinAbs) / range) * 100;
    const maxPct = ((safeMax - this.priceMinAbs) / range) * 100;
    if (this.priceRangeEl) {
      this.priceRangeEl.style.left = minPct + '%';
      this.priceRangeEl.style.right = 100 - maxPct + '%';
    }
    if (this.priceSlider) {
      this.priceSlider.style.setProperty('--range-min', minPct + '%');
      this.priceSlider.style.setProperty('--range-max', maxPct + '%');
    }
    if (this.priceThumbs.min) this.priceThumbs.min.style.left = minPct + '%';
    if (this.priceThumbs.max) this.priceThumbs.max.style.left = maxPct + '%';
  }

  open(triggeredBy) {
    this.trigger = triggeredBy || null;
    this.setAttribute('data-drawer-state', 'open');
    document.body.classList.add('filter-drawer-open');
    setTimeout(() => {
      if (this.panel) this.panel.focus();
    }, 50);
  }

  close() {
    this.setAttribute('data-drawer-state', 'closed');
    document.body.classList.remove('filter-drawer-open');
    if (this.trigger && typeof this.trigger.focus === 'function') this.trigger.focus();
  }

  serializeForm() {
    const params = new URLSearchParams();
    const formData = new FormData(this.form);
    for (const [key, value] of formData.entries()) {
      const str = String(value).trim();
      if (str === '') continue;
      // Skip price bounds that equal the absolute min/max (to keep URL clean)
      if (key === 'filter.v.price.gte' && parseFloat(str) <= this.priceMinAbs) continue;
      if (key === 'filter.v.price.lte' && parseFloat(str) >= this.priceMaxAbs) continue;
      params.append(key, str);
    }
    return params;
  }

  submit() {
    if (!this.form) return;
    const params = this.serializeForm();
    const action = this.form.getAttribute('action') || window.location.pathname;
    const query = params.toString();
    const fullUrl = query ? `${action}?${query}` : action;

    this.setLoading(true);
    const sectionParam = this.sectionId ? `section_id=${encodeURIComponent(this.sectionId)}` : '';
    const fetchUrl = sectionParam
      ? `${action}?${query}${query ? '&' : ''}${sectionParam}`
      : fullUrl;

    fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => r.text())
      .then((html) => {
        this.swapResults(html);
        window.history.replaceState({}, '', fullUrl);
        this.close();
        const resultsEl = document.getElementById('CollectionProductGrid');
        if (resultsEl) {
          resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      })
      .catch((err) => {
        console.error('Filter fetch failed', err);
      })
      .finally(() => this.setLoading(false));
  }

  swapResults(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newResults = doc.getElementById('CollectionProductGrid');
    const currentResults = document.getElementById('CollectionProductGrid');
    if (newResults && currentResults) {
      currentResults.innerHTML = newResults.innerHTML;
    }
    // Update the product count in the filter bar if present
    const newCount = doc.querySelector('.cpg__filter-count');
    const currentCount = document.querySelector('.cpg__filter-count');
    if (newCount && currentCount) currentCount.textContent = newCount.textContent;
  }

  setLoading(isLoading) {
    if (!this.applyBtn) return;
    this.applyBtn.disabled = isLoading;
    if (this.spinner) this.spinner.hidden = !isLoading;
    if (this.applyLabel) this.applyLabel.textContent = isLoading ? 'Učitavanje…' : 'Primeni';
  }
}

customElements.define('filter-drawer', FilterDrawer);
